"use client"

import React from 'react';
import { useState, useEffect, useMemo } from "react"
import api from "../api"
import { useNavigate } from "react-router-dom"
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet-async"
import EventGrid from "../components/EventGrid"
import { useSuccessFailure } from "../context/SuccessFailureContext"
import { useAuth } from "../context/AuthContext"
import { motion, AnimatePresence } from "framer-motion"
import config from "../config"
import { formatMoney } from "../utils/format"
import "./Home.css"

// Relative "x ago" label from a timestamp.
const timeAgo = (ts) => {
  if (!ts) return "recently"
  const d = new Date(String(ts).replace(" ", "T"))
  const secs = Math.floor((Date.now() - d.getTime()) / 1000)
  if (isNaN(secs)) return "recently"
  if (secs < 60) return "just now"
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

// Public price label from tiers / flat price, in the event currency.
const priceLabel = (event) => {
  const currency = event.currency || 'KES'
  const tiers = (event.ticket_tiers || []).filter((t) => t.is_active !== false)
  if (tiers.length > 0) {
    const min = Math.min(...tiers.map((t) => Number(t.price || 0)))
    return min > 0 ? `From ${formatMoney(min, currency)}` : 'Free'
  }
  if (event.is_paid_event && Number(event.ticket_price) > 0) return formatMoney(event.ticket_price, currency)
  return 'Free'
}

const imgUrl = (e) =>
  e.thumbnail_photo
    ? (String(e.thumbnail_photo).startsWith('http') ? e.thumbnail_photo : `${config.MEDIA_BASE_URL}${e.thumbnail_photo}`)
    : null

const Home = () => {
  // Initialize state
  const [visibleEvents, setVisibleEvents] = useState(9)
  const [loading, setLoading] = useState(true)
  const [isLoaded, setIsLoaded] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [events, setEvents] = useState([])
  const [invitations, setInvitations] = useState([])
  const [guests, setGuests] = useState([])
  const [error, setError] = useState(null)

  // Dependencies
  const navigate = useNavigate()
  const { showSuccessModal, showFailureModal } = useSuccessFailure()
  const { user, loading: authLoading } = useAuth()

  // Environment variables
  const faviconUrl = process.env.REACT_APP_FAVICON_URL || "/eventsawa.jpg"
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || "EventSawa"
  const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || "We make events sawa"

  // Combined effect for loading and data fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setIsLoaded(false);
        setError(null);

        const eventsResponse = await api.get('/events/all/');

        let eventsData = [];
        if (Array.isArray(eventsResponse.data)) {
          eventsData = eventsResponse.data;
        } else if (eventsResponse.data && Array.isArray(eventsResponse.data.events)) {
          eventsData = eventsResponse.data.events;
        }

        const filteredEvents = eventsData.filter(event => {
          if (!event) return false;
          if (!event.visibility) return false;
          if (!user) return event.visibility === 'public';
          return event.visibility === 'public' ||
            (event.created_by && (typeof event.created_by === 'object' ? event.created_by.id === user.id : event.created_by === user.id)) ||
            (event.guests && Array.isArray(event.guests) && event.guests.some(guest =>
              guest && (typeof guest === 'object' ? guest.guest === user.id : guest === user.id)
            ));
        });

        setEvents(filteredEvents);

        if (user) {
          try {
            const response = await api.get('/invitations/bulk/');
            setInvitations(response.data.invitations || []);
            setGuests(response.data.guests || []);
          } catch (err) {
            setError("Failed to fetch invitations and guests");
          }
        }

        setLoading(false);
        setIsLoaded(true);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to fetch events");
        setLoading(false);
        setIsLoaded(true);
      }
    };

    setEvents([]);
    setInvitations([]);
    setGuests([]);

    fetchData();

    return () => {
      setLoading(false);
      setIsLoaded(true);
      setError(null);
    };
  }, [user]);

  // Fetch invitations and guests for the current event
  const fetchBulkInvitationsAndGuests = async () => {
    if (!user) return

    try {
      const response = await api.get('/invitations/bulk/');
      const data = response.data
      if (data) {
        setInvitations(data.invitations || [])
        setGuests(data.guests || [])
      } else {
        showFailureModal("Failed to fetch invitations and guests.")
      }
    } catch (error) {
      showFailureModal("Error fetching invitations and guests.")
    }
  }

  // Fetch invitations and guests after events are loaded
  useEffect(() => {
    if (user && events.length > 0) {
      fetchBulkInvitationsAndGuests()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, user])

  // Featured = soonest upcoming public events (with a picture first).
  const featured = useMemo(() => {
    const start = new Date(new Date().toDateString())
    return (events || [])
      .filter((e) => e.visibility === 'public' && e.date && new Date(e.date) >= start)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 8)
  }, [events])

  const cityCount = useMemo(() => {
    const set = new Set()
    ;(events || []).forEach((e) => {
      const loc = (e.location || '').trim()
      if (loc) set.add(loc.includes(',') ? loc.split(',').pop().trim() : loc)
    })
    return set.size
  }, [events])

  // Featured slideshow — 3 events, shown two at a time, auto-scrolling.
  const slides = useMemo(() => featured.slice(0, 3), [featured])
  const [slide, setSlide] = useState(0)
  useEffect(() => {
    if (slides.length < 2) return
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 3500)
    return () => clearInterval(t)
  }, [slides.length])
  const pair = slides.length
    ? [slides[slide % slides.length], slides[(slide + 1) % slides.length]].filter(Boolean)
    : []

  const scrollToEvents = () =>
    document.getElementById('events-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  const handleShowMore = () => setVisibleEvents(visibleEvents + 9)
  const handleEventClick = (eventSlug) => navigate(`/events/${eventSlug}`)

  const popular = ['Music', 'Conference', 'Wedding', 'Sports', 'Tech', 'Comedy', 'Church']

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="esawa-sweepbar" />
        <div className="max-w-5xl mx-auto px-4 py-10 space-y-6">
          <div className="esawa-shimmer h-64 w-full rounded-3xl" />
          <div className="esawa-shimmer h-8 w-1/2 rounded-md" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="esawa-shimmer h-40 rounded-2xl" />)}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{eventTitle} - Discover events near you</title>
        <meta name="description" content={eventDescription} />
        <link rel="icon" href={faviconUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={eventTitle} />
        <meta property="og:description" content={eventDescription} />
        <meta property="og:image" content="/eventsawa.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* ===== World-class discovery hero ===== */}
      <section className="relative overflow-hidden -mt-24 pt-24">
        {/* Vivid gradient backdrop */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 dark:from-indigo-950 dark:via-purple-950 dark:to-fuchsia-950" />
        {/* Glow blobs */}
        <div className="absolute -top-24 -right-16 w-[28rem] h-[28rem] bg-pink-400/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-20 w-[28rem] h-[28rem] bg-indigo-400/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 left-1/2 w-72 h-72 bg-yellow-300/20 rounded-full blur-3xl pointer-events-none" />
        {/* Subtle grid */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hero-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M0 0 L40 0 L40 40 L0 40 Z" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-grid)" />
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            {/* Left: copy + search */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 16 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs font-semibold ring-1 ring-white/25">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                Discover events happening near you
              </span>

              <motion.h1
                className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]"
                initial="hidden"
                animate="show"
                variants={{ show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } } }}
              >
                {"Find your next".split(" ").map((w, i) => (
                  <motion.span
                    key={i}
                    className="inline-block mr-[0.25em]"
                    variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                  >
                    {w}
                  </motion.span>
                ))}
                <br />
                <motion.span
                  className="esawa-animated-gradient inline-block"
                  variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                >
                  unforgettable event.
                </motion.span>
              </motion.h1>

              <p className="mt-4 text-base sm:text-lg text-white/85 max-w-lg">
                Concerts, conferences, weddings and meetups — book instantly with M-Pesa and breeze
                through the door.
              </p>

              {/* Search */}
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && scrollToEvents()}
                    placeholder="Search events, cities, categories…"
                    className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white/95 text-gray-900 placeholder-gray-400 shadow-xl focus:outline-none focus:ring-4 focus:ring-white/30"
                  />
                </div>
                <button
                  onClick={scrollToEvents}
                  className="px-6 py-3.5 rounded-2xl bg-gray-900 text-white font-semibold shadow-xl hover:bg-black transition-colors"
                >
                  Explore
                </button>
              </div>

              {/* Popular searches */}
              <div className="mt-4 flex flex-wrap gap-2">
                {popular.map((t) => (
                  <button
                    key={t}
                    onClick={() => { setSearchQuery(t); scrollToEvents() }}
                    className="px-3 py-1.5 rounded-full bg-white/15 text-white text-sm ring-1 ring-white/25 hover:bg-white/25 transition-colors"
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Stats */}
              <div className="mt-7 flex gap-8">
                <div className="text-white">
                  <div className="text-2xl font-extrabold">{events.length}+</div>
                  <div className="text-white/70 text-xs">Events</div>
                </div>
                <div className="text-white">
                  <div className="text-2xl font-extrabold">{cityCount}+</div>
                  <div className="text-white/70 text-xs">Cities</div>
                </div>
                <div className="text-white">
                  <div className="text-2xl font-extrabold">M-Pesa</div>
                  <div className="text-white/70 text-xs">Instant checkout</div>
                </div>
              </div>
            </motion.div>

            {/* Right: featured events fill the space */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.55 }}
            >
              {slides.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-white font-bold text-lg flex items-center gap-2">✨ Featured</h2>
                    <button onClick={scrollToEvents} className="text-white/80 text-sm font-medium hover:text-white">
                      See all →
                    </button>
                  </div>

                  {/* Two events at a time, auto-scrolling through the set */}
                  <div className="relative overflow-hidden">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={slide}
                        initial={{ opacity: 0, x: 60 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -60 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="grid grid-cols-2 gap-3 sm:gap-4"
                      >
                        {pair.map((e, idx) => (
                          <button
                            key={`${slide}-${e.slug}-${idx}`}
                            onClick={() => handleEventClick(e.slug)}
                            className="group text-left rounded-2xl overflow-hidden bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-black/5 hover:-translate-y-1 transition-transform duration-300"
                          >
                            <div className="relative h-40 sm:h-48">
                              {imgUrl(e) ? (
                                <img src={imgUrl(e)} alt={e.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-indigo-400 to-purple-500" />
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                              {e.date && (
                                <div className="absolute top-2 left-2 bg-white/95 rounded-lg px-2 py-1 text-center shadow leading-none">
                                  <div className="text-base font-extrabold text-gray-900">{new Date(e.date).getDate()}</div>
                                  <div className="text-[9px] font-bold uppercase text-indigo-600">
                                    {new Date(e.date).toLocaleString('en-US', { month: 'short' })}
                                  </div>
                                </div>
                              )}
                              <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-white/90 text-gray-900 text-xs font-bold shadow">
                                {priceLabel(e)}
                              </span>
                              <div className="absolute bottom-2 left-2 right-2 text-white font-bold text-sm line-clamp-1 drop-shadow">
                                {e.title}
                              </div>
                            </div>
                            <div className="px-3 py-2 text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1">
                              📍 {e.location || 'TBA'}
                            </div>
                          </button>
                        ))}
                      </motion.div>
                    </AnimatePresence>

                    {/* Dots */}
                    {slides.length > 1 && (
                      <div className="mt-3 flex justify-center gap-1.5">
                        {slides.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setSlide(i)}
                            aria-label={`Featured ${i + 1}`}
                            className={`h-2 rounded-full transition-all ${i === (slide % slides.length) ? 'w-6 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="hidden lg:flex items-center justify-center h-64 rounded-3xl bg-white/10 ring-1 ring-white/20 text-white/70">
                  Featured events will appear here
                </div>
              )}
            </motion.div>
          </div>
        </div>

        {/* Wave divider into the events section */}
        <div className="absolute bottom-0 left-0 right-0 leading-none">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 60" className="w-full h-auto" preserveAspectRatio="none">
            <path fill="currentColor" className="text-gray-50 dark:text-gray-900"
              d="M0,32L60,37.3C120,43,240,53,360,53.3C480,53,600,43,720,37.3C840,32,960,32,1080,37.3C1200,43,1320,53,1380,58.7L1440,64L1440,64L1380,64C1320,64,1200,64,1080,64C960,64,840,64,720,64C600,64,480,64,360,64C240,64,120,64,60,64L0,64Z" />
          </svg>
        </div>
      </section>

      {/* Events Section */}
      <div id="events-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <EventGrid
          events={events}
          invitations={invitations}
          user={user}
          visibleEvents={visibleEvents}
          onEventClick={handleEventClick}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onShowMore={handleShowMore}
        />
      </div>

      {/* Features Section */}
      <section className="py-12 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Everything you need for successful events
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Our platform provides all the tools you need to create, manage, and measure the success of your events.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                ),
                title: "Event Creation",
                description: "Create beautiful event pages with customizable templates, ticketing options, and RSVP management.",
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
                title: "Guest Management",
                description: "Manage invitations, track RSVPs, assign seating, and communicate with attendees in real-time.",
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
                title: "Analytics & Insights",
                description: "Get detailed analytics on attendance, engagement, and feedback to measure the success of your events.",
              },
            ].map((feature, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-1">
              <a href="/" className="flex items-center mb-6">
                <img src={`${process.env.PUBLIC_URL}/eventsawa.jpg`} className="mr-3 h-9 w-9 rounded-full object-cover" alt="EventSawa Logo" />
                <div className="flex flex-col">
                  <span className="text-xl font-bold font-sans whitespace-nowrap text-gray-900 dark:text-white">{eventTitle}</span>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{eventDescription}</span>
                </div>
              </a>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                The premiere platform for event planning, management, and execution. Create memorable experiences with ease.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:col-span-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase dark:text-white mb-4">Resources</h3>
                <ul className="space-y-3">
                  {["Documentation"].map((item) => (
                    <li key={item}>
                      <Link to={item === 'Documentation' ? '/documentation' : '#'} className="text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition-colors">
                        {item}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase dark:text-white mb-4">Company</h3>
                <ul className="space-y-3">
                  {["About Us"].map((item) => (
                    <li key={item}>
                      <Link to={item === 'About Us' ? '/about' : '#'} className="text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition-colors">
                        {item}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase dark:text-white mb-4">Legal</h3>
                <ul className="space-y-3">
                  {["Privacy Policy", "Terms of Service", "Delete Account"].map((item) => (
                    <li key={item}>
                      <Link
                        to={item === 'Privacy Policy' ? '/privacy-policy' : item === 'Terms of Service' ? '/terms-of-service' : '/delete-account'}
                        className="text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition-colors"
                      >
                        {item}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                © {new Date().getFullYear()} {eventTitle}. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Home
